import React, { FC, useEffect, useMemo, useState } from "react";
import styles from "./geometry-editing.module.scss";
import CustomScrollbar from "../../../ui-kit/scrollbar/scrollbar";
import CoordinatesBlock, {
  ValidationFlags,
} from "../../../../shared/coordinates-block/coordinates-block";
import {
  clearScreenshot,
  deletePlot,
  FormationTypeNames,
  resetFormDataEditDetails,
  resetNewGeometry,
  setBlockNavigation,
  setCoordinatesData,
  setGeometries,
  setKrookiePlots,
} from "../../../../services/reducers/master-plan-modification";
import {
  getGeometryForContour,
  setEditingBorders,
} from "../../../../services/actions/master-plan-modification";
import Button from "../../../ui-kit/button/button";
import LoaderPortal from "../../../../shared/loader-portal/loader-portal";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import {
  MapSection,
  stopDrawingTools,
} from "../../../../services/reducers/map";
import {
  focusOnFeature,
  takeScreenshotAsync,
} from "../../../../services/actions/map";
import { convertPlotsToCoordinatesTable } from "../../../../utils/master-plan-helper";
import { updateVisibleLayers } from "../../../../utils/krookie-helpers";
import Geometry from "@arcgis/core/geometry/Geometry";
import plusIcon from "../../../../images/plus-white.svg";

interface GeometryEditingProps {
  modalMessages: {
    positive: string;
    negative: string;
    params?: Record<string, any>;
  };
  modalTitle: string;
  validationFlags: ValidationFlags;
  addEmptyContourOnMount?: boolean;
  shouldFocusOnFeature?: boolean;
  visibleAddNewPlotBtn?: boolean;
  visibleDeletePlotBtn?: boolean;
}

const GeometryEditing: FC<GeometryEditingProps> = ({
  modalMessages,
  modalTitle,
  validationFlags = {},
  addEmptyContourOnMount,
  shouldFocusOnFeature = true,
  visibleAddNewPlotBtn = false,
  visibleDeletePlotBtn,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [verified, setVerified] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    krookiePlots,
    data,
    selectedPlots,
    selectedFormationType,
    convertedKrookieData,
  } = useAppSelector((state) => state.masterPlanModification);
  const {
    mapComponentReady,
    visibleLayersId,
    layersIds,
    drawingToolsIsActive,
  } = useAppSelector((state) => state.map);

  const initialData = useMemo(
    () => convertPlotsToCoordinatesTable(krookiePlots),
    [krookiePlots],
  );

  const hasKrookies = Object.keys(convertedKrookieData.krookies).length > 0;

  useEffect(() => {
    if (mapComponentReady === MapSection.masterPlan) {
      dispatch(setEditingBorders("draw"));
      if (shouldFocusOnFeature || hasKrookies) {
        if (selectedFormationType === FormationTypeNames.Divide) {
          dispatch(focusOnFeature({ ...selectedPlots[0], label: "" }));
        } else {
          dispatch(focusOnFeature({ ...krookiePlots[0], label: "" }));
        }
      }
    }
  }, [mapComponentReady, shouldFocusOnFeature]);

  useEffect(() => {
    dispatch(setBlockNavigation(true));

    return () => {
      dispatch(setBlockNavigation(false));
    };
  }, []);

  const handleClickNext = () => {
    setIsLoading(true);

    updateVisibleLayers(layersIds, visibleLayersId, dispatch);

    dispatch(clearScreenshot());

    if (drawingToolsIsActive) {
      dispatch(stopDrawingTools());
    }

    const screenshotParamsArray = krookiePlots.flatMap((plot) =>
      [-3, -2].map((zoomAdjustment) => ({
        zoomAdjustment,
        geometry: plot.newGeometry as Geometry,
        objectId: plot.objectId,
      })),
    );

    dispatch(takeScreenshotAsync(screenshotParamsArray))
      .then(() => {
        navigate("details");
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleClickReturn = () => {
    dispatch(stopDrawingTools());
    dispatch(resetNewGeometry());
    dispatch(setEditingBorders("reset"));
    dispatch(resetFormDataEditDetails());
    navigate(-1);
  };

  const handleResetChanges = (id: number) => {
    dispatch(stopDrawingTools());
    dispatch(resetNewGeometry(id));
    dispatch(setEditingBorders("resetAndDraw"));
  };

  const handleDeletePlot = (id: number) => {
    dispatch(deletePlot(id));
  };

  const handleAddNewPlot = () => {
    const wkid = selectedPlots[0].geometry.spatialReference.wkid;

    dispatch(
      setKrookiePlots({
        wkid,
        count: 1,
        append: true,
      }),
    );
  };

  const modalMessage = verified
    ? t(modalMessages.positive, modalMessages.params)
    : t(modalMessages.negative, modalMessages.params);

  return (
    <>
      <p className={styles.headerDesc}>
        {t("editing-block.headerDescription")}
      </p>
      {visibleAddNewPlotBtn && (
        <Button
          onClick={handleAddNewPlot}
          className={styles.addPlotBtn}
          type="withIcon"
        >
          <img src={plusIcon} alt="Add New Plot" /> Add New Plot
        </Button>
      )}
      <div className={styles.scrollContent}>
        <CustomScrollbar>
          <div className={styles.coordinatesWrapper}>
            <CoordinatesBlock
              initialData={initialData}
              verified={verified}
              setVerified={setVerified}
              onSetGeometries={({ newGeometry, stringGeometry, clearSelection }) =>
                dispatch(setGeometries({ newGeometry, stringGeometry, clearSelection }))
              }
              onSetCoordinatesData={(data) =>
                dispatch(setCoordinatesData(data))
              }
              onGetGeometry={(plotIdx, contourIdx) =>
                dispatch(getGeometryForContour({ plotIdx, contourIdx }))
              }
              validationOptions={{
                flags: validationFlags,
                data: {
                  current: krookiePlots,
                  all: data,
                  initial: selectedPlots,
                },
                ui: {
                  modalMessage: modalMessage,
                  modalTitle: t(modalTitle),
                  onResetChanges: handleResetChanges,
                },
              }}
              addEmptyContourOnMount={addEmptyContourOnMount}
              visibleDeletePlotBtn={visibleDeletePlotBtn}
              onDeletePlot={(id) => handleDeletePlot(id)}
              disableResetOnDrawingTools={false}
            />
          </div>
        </CustomScrollbar>
      </div>
      <div className={styles.buttonSection}>
        <Button
          type="primary"
          disabled={!verified}
          className={`${styles.button} ${styles.next}`}
          onClick={handleClickNext}
        >
          {t("main-form-create-study.nextButton")}
        </Button>
        <Button
          type="outline"
          className={`${styles.button} ${styles.cancel}`}
          onClick={handleClickReturn}
        >
          {t("main-form-create-study.returnButton")}
        </Button>
      </div>
      {isLoading && <LoaderPortal />}
    </>
  );
};

export default GeometryEditing;
